import { defineStore } from 'pinia'
import { siteSettingsService } from '@/services/siteSettingsService'

const lt = (vi, en) => ({ vi, en })

/**
 * Site-wide branding/header/footer/homepage content, loaded once from the
 * public GET /api/v1/site-settings (see App.vue). Defaults here mirror the
 * backend's own defaults (services/site_settings.go) so the page never
 * looks broken during that first load.
 *
 * Header/footer/homepage copy is admin-entered per-language ({vi, en}) since
 * it's free text the i18n catalog can't auto-translate — see localize().
 */
export const useSiteSettingsStore = defineStore('siteSettings', {
  state: () => ({
    loaded: false,
    branding: {
      site_name: 'SmartFarm',
      logo_url: '',
      favicon_url: '',
      primary_color: '#16a34a',
      secondary_color: '#0f3d24'
    },
    header: {
      tagline: lt('Quản lý trang trại heo', 'Pig farm management'),
      nav_items: [
        { label: lt('Tính năng', 'Features'), href: '#features' },
        { label: lt('Phân hệ', 'Modules'), href: '#modules' }
      ]
    },
    footer: {
      text: lt('', ''),
      copyright: 'SmartFarm',
      links: []
    },
    login: {
      banner_url: '',
      title: lt('Quản lý trang trại chăn nuôi heo', 'Pig farm management'),
      slogans: [lt('Gọn gàng.', 'Organized.'), lt('Rõ ràng.', 'Clear.'), lt('Hiệu quả hơn.', 'More efficient.')]
    },
    homepage: {
      hero_eyebrow: lt('Quản lý trang trại heo', 'Pig farm management'),
      hero_title: lt('Nuôi heo gọn gàng,\nsố liệu rõ ràng.', 'Pig farming, organized.\nClear data, clear decisions.'),
      hero_subtitle: lt('Theo dõi đàn nái, lứa heo, thú y và vật tư trên một hệ thống duy nhất.', 'Track sows, litters, vet care and supplies in one system.'),
      hero_cta_label: lt('Bắt đầu ngay', 'Get started'),
      features: [],
      modules: []
    }
  }),

  actions: {
    async load() {
      try {
        const data = await siteSettingsService.getAll()
        // Keep the defaults for any section the API omits (empty/partial payload).
        if (data && typeof data === 'object') {
          for (const key of ['branding', 'header', 'footer', 'homepage', 'login']) {
            const v = data[key]
            if (v && typeof v === 'object' && !Array.isArray(v)) {
              this[key] = { ...this[key], ...v }
            }
          }
        }
      } catch {
        // Public endpoint on a fresh/offline backend — keep the defaults above
        // rather than breaking the page.
      } finally {
        this.loaded = true
      }
    }
  }
})

/** Picks `locale` ('vi' | 'en') from a {vi, en} field, falling back to VI. */
export function localize(field, locale) {
  if (!field) return ''
  return (locale === 'en' && field.en) ? field.en : (field.vi || field.en || '')
}
